// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect } from 'react';
import { selectAvailableParametersSectors } from '@advisor-app/store/slices/available-parameters';
import { PageContainer, Tabs, useTabsController } from '@shared/ui-kit/components';
import { ETFs, Equity, MutualFunds } from '@advisor-app/features/screener';
import {
  SavedPortfolios,
  Screeners,
  Watchlist,
} from '@advisor-app/features/my-research';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  selectSectorsFilter,
  updateUniverseFilters,
} from '@advisor-app/store/slices/parameters';
import { useSearchParams } from 'react-router';
import { getScreeners } from '@advisor-app/store/slices/my-research';
import { Marketplace } from '@advisor-app/features/marketplace';
import { Comparison } from '@advisor-app/features/comparison';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { setWorkflow } from '@advisor-app/store/slices/workflow';

export const Research = () => {
  const dispatch = useAppDispatch();

  const sectors = useAppSelector(selectAvailableParametersSectors);
  const sectorsFilter = useAppSelector(state =>
    selectSectorsFilter(state, 'screener'),
  );
  const [params, setParams] = useSearchParams();
  const tabsController = useTabsController(
    params.get('tab') || undefined,
    undefined,
    tab => {
      setParams({ tab: tab });
    },
    [params.get('tab')],
  );

  const onLoadScreener = useCallback(
    () => tabsController.setTab('screeners'),
    [tabsController],
  );

  const onSaveScreener = useCallback(() => {
    dispatch(getScreeners());

    tabsController.setTab('my-research');
  }, [dispatch, tabsController]);

  useEffect(() => {
    if (sectorsFilter.length === 0) {
      dispatch(updateUniverseFilters(['screener', { sectors_filter: sectors }]));
    }

    dispatch(
      setWorkflow({
        type: '',
        asset: '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer className="!mt-0">
      <Tabs.Root controller={tabsController} defaultValue="my-research">
        <Tabs.List>
          <Tabs.Trigger value="my-research">
            <Translation i18nKey="research.tabs.my-research" ns="research" />
          </Tabs.Trigger>
          <Tabs.Trigger value="screeners">
            <Translation i18nKey="research.tabs.screeners" ns="research" />
          </Tabs.Trigger>
          <Tabs.Trigger value="comparison">
            <Translation i18nKey="research.tabs.comparison" ns="research" />
          </Tabs.Trigger>
          <Tabs.Trigger value="marketplace">
            <Translation i18nKey="research.tabs.marketplace" ns="research" />
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="my-research">
          <div className="mt-24 grid gap-4">
            <Watchlist />
            <SavedPortfolios />
            <Screeners onSelectScreener={onLoadScreener} />
          </div>
        </Tabs.Content>

        <Tabs.Content value="screeners">
          <Tabs.Root
            defaultValue="equity"
            className="mt-[28px]"
            state-id="screeners"
            variant="underline"
          >
            <Tabs.List>
              <Tabs.Trigger value="equity">
                <Translation i18nKey="research.tabs.stocks" ns="research" />
              </Tabs.Trigger>
              <Tabs.Trigger value="etf">
                <Translation i18nKey="research.tabs.etfs" ns="research" />
              </Tabs.Trigger>
              <Tabs.Trigger value="mutual funds">
                <Translation i18nKey="research.tabs.mutual-funds" ns="research" />
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="equity">
              <Equity onSaveScreener={onSaveScreener} id="screener" />
            </Tabs.Content>

            <Tabs.Content value="mutual funds">
              <MutualFunds />
            </Tabs.Content>

            <Tabs.Content value="etf">
              <ETFs />
            </Tabs.Content>
          </Tabs.Root>
        </Tabs.Content>

        <Tabs.Content value="marketplace">
          <Marketplace />
        </Tabs.Content>

        <Tabs.Content value="comparison">
          <Comparison />
        </Tabs.Content>
      </Tabs.Root>
    </PageContainer>
  );
};

export default Research;
